import type { AgendaItemInput, LegalStructure, NoticeRule } from './contracts';

// Standard agendas and notice periods. Titles are in French: the syndicate's records are kept in French.
// The legal defaults (notice periods, items required by the Civil Code) are to be validated by a condo lawyer;
// each syndicate's declaration, or each association's by-laws, may add requirements.

type Kind = 'annual' | 'special' | 'board';
type Template = AgendaItemInput & { fixedEnd?: boolean; only?: LegalStructure };

const OPENING: Template[] = [
  { title: 'Ouverture de l’assemblée et vérification du quorum', kind: 'procedure' },
  { title: 'Nomination d’un président et d’un secrétaire d’assemblée', kind: 'procedure' },
  { title: 'Adoption de l’ordre du jour', kind: 'resolution', majority: 'simple', proposedResolution: 'Il est résolu d’adopter l’ordre du jour tel que présenté.' },
];
const CLOSING: Template[] = [
  { title: 'Varia', kind: 'discussion', fixedEnd: true },
  { title: 'Levée de l’assemblée', kind: 'procedure', fixedEnd: true },
];

const TEMPLATES: Record<Kind, Template[]> = {
  annual: [
    ...OPENING,
    { title: 'Approbation du procès-verbal de la dernière assemblée', kind: 'resolution', majority: 'simple', proposedResolution: 'Il est résolu d’approuver le procès-verbal de la dernière assemblée.' },
    { title: 'Rapport du conseil d’administration', kind: 'information' },
    { title: 'Présentation des états financiers', kind: 'information' },
    { title: 'Consultation sur le budget et les contributions de l’exercice', kind: 'discussion', only: 'divided_coownership' },
    { title: 'Adoption du budget de l’exercice', kind: 'resolution', majority: 'simple', only: 'npo_with_servitudes' },
    { title: 'Rapport sur le fonds de prévoyance et le carnet d’entretien', kind: 'information', only: 'divided_coownership' },
    { title: 'Élection des administrateurs', kind: 'election' },
    ...CLOSING,
  ],
  special: [...OPENING, ...CLOSING],
  board: [
    { title: 'Ouverture de la réunion et constatation du quorum', kind: 'procedure' },
    { title: 'Adoption de l’ordre du jour', kind: 'resolution', majority: 'simple', proposedResolution: 'Il est résolu d’adopter l’ordre du jour tel que présenté.' },
    { title: 'Approbation du procès-verbal de la dernière réunion', kind: 'resolution', majority: 'simple' },
    { title: 'Suivi des actions de la dernière réunion', kind: 'information' },
    { title: 'Varia', kind: 'discussion', fixedEnd: true },
    { title: 'Levée de la réunion', kind: 'procedure', fixedEnd: true },
  ],
};

export function standardAgenda(kind: Kind, structure: LegalStructure): (AgendaItemInput & { fixedEnd: boolean })[] {
  return TEMPLATES[kind]
    .filter((t) => !t.only || t.only === structure)
    .map(({ only: _only, fixedEnd, ...item }) => ({ ...item, source: 'statutory' as const, fixedEnd: fixedEnd ?? false }));
}

/** Default title of a new meeting. */
export function defaultTitle(kind: Kind, structure: LegalStructure, year: number): string {
  if (kind === 'board') return 'Réunion du conseil d’administration';
  const who = structure === 'divided_coownership' ? 'des copropriétaires' : 'des membres';
  return kind === 'annual' ? `Assemblée générale annuelle ${who} ${year}` : `Assemblée extraordinaire ${who}`;
}

/** Notice period, in days before the meeting (to validate; the declaration or by-laws may set others). */
export function noticeRule(kind: Kind, structure: LegalStructure): NoticeRule {
  if (kind === 'board') return { minDays: null, maxDays: null };
  return structure === 'divided_coownership' ? { minDays: 10, maxDays: 30 } : { minDays: 10, maxDays: null };
}
